import React, { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { api, maskCPF, maskPhone, DOC_TYPE_LABELS, REQUIRED_DOCS, OPTIONAL_DOCS } from "../lib/api";
import { Upload, Check, Loader2, AlertCircle, FileCheck, MapPin, ShieldAlert } from "lucide-react";
import { ForteShield } from "../components/BrandLogo";

const inputClass = "fc-input";

function Field({ label, children, required, hint, full }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="fc-label">{label}{required && <span className="text-[#D4AF37] ml-0.5">*</span>}</label>
      {children}
      {hint && <div className="text-[11px] text-[#737373] mt-1">{hint}</div>}
    </div>
  );
}

export default function PublicRegistrationPage() {
  const [sp] = useSearchParams();
  const code = sp.get("indicador") || sp.get("ind") || "";
  const [indicator, setIndicator] = useState(null);
  const [loadingInd, setLoadingInd] = useState(true);

  const [form, setForm] = useState({
    full_name: "", cpf: "", birth_date: "", whatsapp: "",
    city: "", state: "", address: "", profession: "",
    monthly_income: "", company_name: "", employment_time: "",
    requested_amount: "", purpose: "", client_notes: "",
    // Referências pessoais (obrigatórias)
    ref1_name: "", ref1_parentesco: "", ref1_whatsapp: "",
    ref2_name: "", ref2_parentesco: "", ref2_whatsapp: "",
  });
  const [files, setFiles] = useState({}); // key: doc_type -> File
  const [step, setStep] = useState("form"); // form | uploading | done | error
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [errorStage, setErrorStage] = useState(""); // dados | upload | conexao | servidor
  const [progress, setProgress] = useState({ done: 0, total: 0, currentDoc: "" });
  // Quando o cliente é criado mas algum upload falha, guardamos o id para retomar do ponto onde parou.
  const [partialClientId, setPartialClientId] = useState(null);
  const [pendingDocs, setPendingDocs] = useState([]); // lista de doc_types ainda não enviados
  // Idempotency key — gerada UMA vez por sessão para o backend deduplicar reenvios
  const [idempotencyKey] = useState(() => {
    try {
      return (window.crypto && window.crypto.randomUUID)
        ? window.crypto.randomUUID()
        : `idem-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
    } catch (_) {
      return `idem-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
    }
  });

  // Geolocation — obrigatório
  const [geo, setGeo] = useState({
    status: "idle", // idle | pending | granted | denied | unavailable
    latitude: null,
    longitude: null,
    accuracy: null,
    error: "",
  });

  const captureLocation = () => {
    setError("");
    if (typeof navigator === "undefined" || !("geolocation" in navigator)) {
      setGeo({
        status: "unavailable",
        latitude: null, longitude: null, accuracy: null,
        error: "Não conseguimos capturar sua localização. Ative a localização do celular e tente novamente.",
      });
      return;
    }
    setGeo(prev => ({ ...prev, status: "pending", error: "" }));
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setGeo({
          status: "granted",
          latitude: pos.coords.latitude,
          longitude: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
          error: "",
        });
      },
      (err) => {
        // PERMISSION_DENIED = 1
        if (err && err.code === 1) {
          setGeo({
            status: "denied",
            latitude: null, longitude: null, accuracy: null,
            error: "Para concluir seu cadastro, é necessário permitir o acesso à localização.",
          });
        } else {
          setGeo({
            status: "unavailable",
            latitude: null, longitude: null, accuracy: null,
            error: "Não conseguimos capturar sua localização. Ative a localização do celular e tente novamente.",
          });
        }
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  };

  useEffect(() => {
    if (!code) { setLoadingInd(false); return; }
    api.get(`/indicators/by-code/${code}`)
      .then(({ data }) => setIndicator(data))
      .catch(() => setIndicator(null))
      .finally(() => setLoadingInd(false));
  }, [code]);

  const update = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const allRequiredDocsSelected = useMemo(() => REQUIRED_DOCS.every(d => files[d]), [files]);

  // Parser robusto de erros vindos da API — sempre devolve string PT-BR para o usuário.
  const parseApiError = (err, fallback = "Não foi possível enviar o cadastro. Tente novamente.") => {
    // Sem resposta = problema de rede / timeout / CORS
    if (!err || !err.response) {
      if (err && err.code === "ECONNABORTED") {
        return "Tempo de envio esgotado. Verifique sua conexão e tente novamente.";
      }
      return "Sem conexão com o servidor. Verifique sua internet e tente novamente.";
    }
    const status = err.response.status;
    const detail = err.response.data?.detail;
    if (typeof detail === "string" && detail.trim()) return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      // Pydantic legado — montamos uma mensagem amigável
      const first = detail[0];
      const field = (first?.loc || []).filter(p => p !== "body").pop() || "campo";
      return `Erro de validação em "${field}": ${first?.msg || "valor inválido"}.`;
    }
    if (status === 413) return "Os arquivos enviados são muito pesados. Reduza a qualidade das fotos e tente novamente.";
    if (status === 502 || status === 503 || status === 504) {
      return "Servidor temporariamente indisponível. Tente novamente em instantes.";
    }
    if (status >= 500) return "Erro interno do servidor. Tente novamente em alguns instantes.";
    return fallback;
  };

  const showStageError = (stage, message) => {
    setErrorStage(stage);
    setError(message);
    setStep("error");
  };

  // Faz upload sequencial de um conjunto de docs. Devolve a lista dos que falharam (para retomada).
  const uploadDocsSequential = async (clientId, docsToUpload) => {
    const failed = [];
    setProgress({ done: 0, total: docsToUpload.length, currentDoc: "" });
    for (let i = 0; i < docsToUpload.length; i++) {
      const [docType, file] = docsToUpload[i];
      setProgress({ done: i, total: docsToUpload.length, currentDoc: DOC_TYPE_LABELS[docType] || docType });
      try {
        const fd = new FormData();
        fd.append("document_type", docType);
        fd.append("file", file);
        await api.post(`/clients/${clientId}/documents`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 120000, // 2 min por arquivo
        });
        setProgress({ done: i + 1, total: docsToUpload.length, currentDoc: "" });
      } catch (err) {
        // Para no primeiro erro — guarda os pendentes (incluindo este) para retry
        const remaining = docsToUpload.slice(i);
        const docLabel = DOC_TYPE_LABELS[docType] || docType;
        const reason = parseApiError(err, `Falha ao enviar "${docLabel}".`);
        failed.push({ docType, reason, remaining });
        return { failed, remaining };
      }
    }
    return { failed: [], remaining: [] };
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting || step === "done") return; // hard guard contra double-submit
    setError("");
    setErrorStage("");
    if (!indicator) { showStageError("dados", "Link inválido ou indicador inativo."); return; }
    if (!allRequiredDocsSelected) { showStageError("dados", "Envie todos os documentos obrigatórios."); return; }
    if (geo.status !== "granted" || geo.latitude == null || geo.longitude == null) {
      showStageError("dados", geo.error || "Para concluir seu cadastro, é necessário permitir o acesso à localização.");
      return;
    }
    // Soma total — alerta amigável se ficar muito pesado (>40MB) — backend aceita 8MB/arq
    const docsList = Object.entries(files);
    const totalBytes = docsList.reduce((acc, [, f]) => acc + (f?.size || 0), 0);
    if (totalBytes > 40 * 1024 * 1024) {
      showStageError("dados", "Os arquivos somam mais de 40MB. Reduza o tamanho das fotos e tente novamente.");
      return;
    }

    setSubmitting(true);
    setStep("uploading");
    setProgress({ done: 0, total: 0, currentDoc: "" });

    let succeeded = false;
    try {
      let clientId = partialClientId;
      let docsToUpload = docsList;

      // Etapa 1 — cria cliente (se ainda não tiver sido criado em tentativa anterior)
      if (!clientId) {
        try {
          const payload = {
            ...form,
            monthly_income: Number(form.monthly_income.toString().replace(/[^\d.,-]/g, "").replace(",", ".")),
            requested_amount: Number(form.requested_amount.toString().replace(/[^\d.,-]/g, "").replace(",", ".")),
            indicator_code: indicator.code,
            indicator_name_captured: indicator.name,
            latitude: geo.latitude,
            longitude: geo.longitude,
            accuracy: geo.accuracy,
            location_permission: "granted",
          };
          const { data } = await api.post("/clients/public", payload, {
            headers: { "Idempotency-Key": idempotencyKey },
            timeout: 60000,
          });
          clientId = data.client_id;
          setPartialClientId(clientId);
        } catch (err) {
          showStageError("dados", parseApiError(err));
          return;
        }
      } else {
        // Retomada — só envia os que faltam
        docsToUpload = docsList.filter(([dt]) => pendingDocs.includes(dt));
        if (docsToUpload.length === 0) docsToUpload = docsList; // segurança
      }

      // Etapa 2 — uploads
      const { failed, remaining } = await uploadDocsSequential(clientId, docsToUpload);
      if (failed.length > 0) {
        const f = failed[0];
        setPendingDocs(remaining.map(([dt]) => dt));
        showStageError("upload", `${f.reason} Você pode tentar reenviar apenas os documentos pendentes sem refazer o cadastro.`);
        return;
      }

      // Etapa 3 — dispara análise antifraude em background (não bloqueia o cliente).
      try {
        await api.post(`/clients/public/${clientId}/trigger-analysis`);
      } catch (_) { /* noop — não impede sucesso */ }

      succeeded = true;
      setPendingDocs([]);
      setStep("done");
    } finally {
      // Em caso de sucesso, NÃO reabilita o botão — fluxo passa para 'done'.
      // Em caso de erro, libera para permitir nova tentativa.
      // O backend deduplica via Idempotency-Key (cliente nunca é criado duas vezes).
      if (!succeeded) setSubmitting(false);
    }
  };

  if (step === "done") {
    return (
      <div className="min-h-screen auth-bg flex items-center justify-center p-6">
        <div className="max-w-lg w-full text-center fc-card p-10" data-testid="public-success">
          <div className="w-14 h-14 rounded-full bg-[#D4AF37]/15 border border-[#D4AF37]/40 flex items-center justify-center mx-auto">
            <Check className="text-[#D4AF37]" size={28} />
          </div>
          <h1 className="font-display text-3xl font-bold mt-6">Cadastro recebido</h1>
          <p className="text-[#A3A3A3] mt-4 leading-relaxed">
            Nossa equipe fará uma análise e entrará em contato pelo WhatsApp.
          </p>
          <div className="gold-line w-24 mx-auto mt-8" />
          <p className="text-xs text-[#737373] mt-6">Forte Capital — Análise Privada</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen auth-bg flex flex-col">
      <header className="border-b border-[#1f1f1f] bg-[#0d0d0d]/80 backdrop-blur sticky top-0 z-20">
        <div className="max-w-5xl mx-auto px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ForteShield size={36}/>
            <div>
              <div className="font-display font-bold text-[15px] tracking-[0.04em]">FORTE CAPITAL</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]">Cadastro Privado</div>
            </div>
          </div>
          {indicator && (
            <div className="hidden sm:flex items-center gap-2 text-[12px] text-[#A3A3A3]">
              <span>Indicador:</span>
              <span className="text-white font-medium" data-testid="indicator-name">{indicator.name}</span>
            </div>
          )}
        </div>
      </header>

      <main className="flex-1 max-w-5xl w-full mx-auto px-5 py-10">
        {loadingInd ? (
          <div className="text-center py-20 text-[#A3A3A3]">Carregando...</div>
        ) : !indicator ? (
          <div className="fc-card p-8 text-center max-w-md mx-auto" data-testid="invalid-link">
            <AlertCircle className="mx-auto text-red-400" size={40}/>
            <h2 className="font-display text-2xl font-bold mt-4">Link inválido</h2>
            <p className="text-[#A3A3A3] mt-3 text-sm">
              Este link de cadastro não foi encontrado ou está inativo. Solicite um novo link ao seu indicador.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-8" data-testid="public-form">
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37]">Etapa única</div>
              <h1 className="font-display text-3xl sm:text-4xl font-bold mt-2 tracking-tight">Solicite sua análise</h1>
              <p className="text-[#A3A3A3] mt-2 max-w-2xl text-[14.5px] leading-relaxed">
                Preencha seus dados com atenção. Após o envio, nossa equipe entrará em contato pelo WhatsApp informado.
              </p>
            </div>

            {/* Dados pessoais */}
            <section className="fc-card p-6 sm:p-8">
              <h2 className="font-display text-lg font-semibold tracking-tight">Dados pessoais</h2>
              <div className="grid sm:grid-cols-2 gap-5 mt-5">
                <Field label="Nome completo" required full>
                  <input className={inputClass} value={form.full_name} onChange={e=>update("full_name", e.target.value)} required data-testid="field-full_name"/>
                </Field>
                <Field label="CPF" required>
                  <input className={inputClass} value={form.cpf} onChange={e=>update("cpf", maskCPF(e.target.value))} required data-testid="field-cpf" inputMode="numeric"/>
                </Field>
                <Field label="Data de nascimento" required>
                  <input type="date" className={inputClass} value={form.birth_date} onChange={e=>update("birth_date", e.target.value)} required data-testid="field-birth_date"/>
                </Field>
                <Field label="WhatsApp" required>
                  <input className={inputClass} value={form.whatsapp} onChange={e=>update("whatsapp", maskPhone(e.target.value))} required data-testid="field-whatsapp" inputMode="tel"/>
                </Field>
                <Field label="Profissão" required>
                  <input className={inputClass} value={form.profession} onChange={e=>update("profession", e.target.value)} required data-testid="field-profession"/>
                </Field>
              </div>
            </section>

            {/* Endereço */}
            <section className="fc-card p-6 sm:p-8">
              <h2 className="font-display text-lg font-semibold tracking-tight">Endereço</h2>
              <div className="grid sm:grid-cols-2 gap-5 mt-5">
                <Field label="Cidade" required>
                  <input className={inputClass} value={form.city} onChange={e=>update("city", e.target.value)} required data-testid="field-city"/>
                </Field>
                <Field label="Estado (UF)" required>
                  <input className={inputClass} maxLength={2} value={form.state} onChange={e=>update("state", e.target.value.toUpperCase())} required data-testid="field-state"/>
                </Field>
                <Field label="Endereço completo" required full hint="Rua, número, bairro, complemento">
                  <textarea className={`${inputClass} resize-none`} rows={2} value={form.address} onChange={e=>update("address", e.target.value)} required data-testid="field-address"/>
                </Field>
              </div>
            </section>

            {/* Profissional */}
            <section className="fc-card p-6 sm:p-8">
              <h2 className="font-display text-lg font-semibold tracking-tight">Renda e trabalho</h2>
              <div className="grid sm:grid-cols-2 gap-5 mt-5">
                <Field label="Renda mensal (R$)" required>
                  <input className={inputClass} value={form.monthly_income} onChange={e=>update("monthly_income", e.target.value)} required data-testid="field-monthly_income" inputMode="decimal"/>
                </Field>
                <Field label="Empresa onde trabalha">
                  <input className={inputClass} value={form.company_name} onChange={e=>update("company_name", e.target.value)} data-testid="field-company_name"/>
                </Field>
                <Field label="Tempo de trabalho">
                  <input className={inputClass} value={form.employment_time} onChange={e=>update("employment_time", e.target.value)} placeholder="Ex: 2 anos" data-testid="field-employment_time"/>
                </Field>
              </div>
            </section>

            {/* Solicitação */}
            <section className="fc-card p-6 sm:p-8">
              <h2 className="font-display text-lg font-semibold tracking-tight">Sobre a solicitação</h2>
              <div className="grid sm:grid-cols-2 gap-5 mt-5">
                <Field label="Valor solicitado (R$)" required>
                  <input className={inputClass} value={form.requested_amount} onChange={e=>update("requested_amount", e.target.value)} required data-testid="field-requested_amount" inputMode="decimal"/>
                </Field>
                <Field label="Finalidade" required>
                  <input className={inputClass} value={form.purpose} onChange={e=>update("purpose", e.target.value)} required data-testid="field-purpose"/>
                </Field>
                <Field label="Observações (opcional)" full>
                  <textarea className={`${inputClass} resize-none`} rows={3} value={form.client_notes} onChange={e=>update("client_notes", e.target.value)} data-testid="field-client_notes"/>
                </Field>
              </div>
            </section>

            {/* Referências pessoais */}
            <section className="fc-card p-6 sm:p-8">
              <h2 className="font-display text-lg font-semibold tracking-tight">Referências pessoais</h2>
              <p className="text-[13px] text-[#A3A3A3] mt-1">
                Informe duas pessoas — <strong className="text-[#E8C547]">preferencialmente parentes próximos</strong> — que possam ser contatadas em caso de necessidade.
              </p>
              <div className="grid sm:grid-cols-3 gap-4 mt-5">
                <Field label="Nome (referência 1)" required>
                  <input className={inputClass} value={form.ref1_name} onChange={e=>update("ref1_name", e.target.value)} required data-testid="field-ref1_name"/>
                </Field>
                <Field label="Parentesco" required hint="Ex: mãe, pai, irmã, primo">
                  <input className={inputClass} value={form.ref1_parentesco} onChange={e=>update("ref1_parentesco", e.target.value)} required data-testid="field-ref1_parentesco"/>
                </Field>
                <Field label="WhatsApp" required hint="(DDD) 9 0000-0000">
                  <input className={inputClass} value={form.ref1_whatsapp} onChange={e=>update("ref1_whatsapp", maskPhone(e.target.value))} required data-testid="field-ref1_whatsapp"/>
                </Field>

                <Field label="Nome (referência 2)" required>
                  <input className={inputClass} value={form.ref2_name} onChange={e=>update("ref2_name", e.target.value)} required data-testid="field-ref2_name"/>
                </Field>
                <Field label="Parentesco" required>
                  <input className={inputClass} value={form.ref2_parentesco} onChange={e=>update("ref2_parentesco", e.target.value)} required data-testid="field-ref2_parentesco"/>
                </Field>
                <Field label="WhatsApp" required>
                  <input className={inputClass} value={form.ref2_whatsapp} onChange={e=>update("ref2_whatsapp", maskPhone(e.target.value))} required data-testid="field-ref2_whatsapp"/>
                </Field>
              </div>
            </section>

            {/* Documentos */}
            <section className="fc-card p-6 sm:p-8">
              <h2 className="font-display text-lg font-semibold tracking-tight">Documentos</h2>
              <p className="text-[13px] text-[#A3A3A3] mt-1">Imagens (JPG/PNG/WebP) ou PDF — até 8MB por arquivo.</p>
              <div className="grid sm:grid-cols-2 gap-4 mt-5">
                {[...REQUIRED_DOCS, ...OPTIONAL_DOCS].map(dt => {
                  const required = REQUIRED_DOCS.includes(dt);
                  const f = files[dt];
                  return (
                    <label key={dt} className={`flex items-center gap-3 p-4 rounded-md border cursor-pointer transition-all ${f ? "border-[#D4AF37]/50 bg-[#D4AF37]/5" : "border-[#262626] hover:border-[#3a3a3a]"}`} data-testid={`upload-${dt}`}>
                      <input
                        type="file"
                        accept="image/*,application/pdf"
                        className="hidden"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          if (file.size > 8 * 1024 * 1024) { setError(`${DOC_TYPE_LABELS[dt]}: arquivo maior que 8MB.`); return; }
                          setFiles(prev => ({ ...prev, [dt]: file }));
                          setError("");
                        }}
                      />
                      <div className="w-10 h-10 rounded-md bg-[#1A1A1A] border border-[#262626] flex items-center justify-center">
                        {f ? <FileCheck className="text-[#D4AF37]" size={18}/> : <Upload className="text-[#A3A3A3]" size={18}/>}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium">
                          {DOC_TYPE_LABELS[dt]} {required && <span className="text-[#D4AF37]">*</span>}
                        </div>
                        <div className="text-[11px] text-[#A3A3A3] truncate">{f ? f.name : "Clique para selecionar"}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </section>

            {/* Localização — obrigatória */}
            <section className="fc-card p-6 sm:p-8" data-testid="geo-section">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-md bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center flex-shrink-0">
                  <MapPin className="text-[#D4AF37]" size={18}/>
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="font-display text-lg font-semibold tracking-tight">Confirmação de localização</h2>
                  <p className="text-[13px] text-[#A3A3A3] mt-1 leading-relaxed">
                    Para finalizar seu cadastro precisamos confirmar sua localização atual.
                    Clique no botão abaixo e <strong className="text-white">permita</strong> o acesso quando o navegador solicitar.
                  </p>
                </div>
              </div>

              <div className="mt-5">
                {geo.status === "granted" ? (
                  <div className="flex items-center gap-3 p-4 rounded-md border border-emerald-500/30 bg-emerald-500/5" data-testid="geo-success">
                    <Check className="text-emerald-400 flex-shrink-0" size={20}/>
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-emerald-300">Localização confirmada</div>
                      <div className="text-[11.5px] text-[#A3A3A3] mt-0.5 font-mono">
                        {geo.latitude.toFixed(6)}, {geo.longitude.toFixed(6)}
                        {geo.accuracy != null && <> · precisão ±{Math.round(geo.accuracy)}m</>}
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={captureLocation}
                      disabled={geo.status === "pending"}
                      className="fc-btn-gold w-full sm:w-auto flex items-center justify-center gap-2 disabled:opacity-60"
                      data-testid="geo-capture-button"
                    >
                      {geo.status === "pending"
                        ? <><Loader2 size={16} className="animate-spin"/> Capturando…</>
                        : <><MapPin size={16}/> Capturar minha localização <span className="text-black/70">*</span></>}
                    </button>
                    {(geo.status === "denied" || geo.status === "unavailable") && geo.error && (
                      <div className="mt-3 flex items-start gap-2 p-3 rounded-md border border-red-500/30 bg-red-500/10 text-sm text-red-300" data-testid="geo-error">
                        <ShieldAlert size={16} className="flex-shrink-0 mt-0.5"/>
                        <span>{geo.error}</span>
                      </div>
                    )}
                  </>
                )}
              </div>
            </section>

            {error && (
              <div
                className={`text-sm rounded-md px-4 py-3 border ${
                  errorStage === "upload"
                    ? "text-amber-200 bg-amber-500/10 border-amber-500/30"
                    : "text-red-400 bg-red-500/10 border-red-500/20"
                }`}
                data-testid="form-error"
              >
                <div className="font-medium mb-1">
                  {errorStage === "dados" && "Não foi possível enviar os dados"}
                  {errorStage === "upload" && "Falha no envio de um documento"}
                  {!errorStage && "Erro"}
                </div>
                <div>{error}</div>
                {errorStage === "upload" && pendingDocs.length > 0 && (
                  <div className="text-[12px] text-amber-300/80 mt-2">
                    Pendentes: {pendingDocs.map(d => DOC_TYPE_LABELS[d] || d).join(", ")}
                  </div>
                )}
              </div>
            )}

            <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-4 pt-2">
              <p className="text-[11.5px] text-[#737373] max-w-md">
                Ao enviar você autoriza a Forte Capital a tratar seus dados e localização para fins de análise. Seus arquivos são armazenados de forma privada.
              </p>
              <button
                type="submit"
                disabled={submitting || geo.status !== "granted"}
                className="fc-btn-gold flex items-center justify-center gap-2 min-w-[220px] disabled:opacity-60 disabled:cursor-not-allowed"
                data-testid="public-submit-button"
              >
                {submitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    {progress.total > 0
                      ? <>Enviando documento {progress.done + (progress.currentDoc ? 1 : 0)} de {progress.total}{progress.currentDoc ? `: ${progress.currentDoc.slice(0, 24)}` : ""}</>
                      : <>Enviando cadastro…</>}
                  </>
                ) : partialClientId && pendingDocs.length > 0 ? (
                  "Tentar reenviar pendentes"
                ) : (
                  "Enviar cadastro"
                )}
              </button>
            </div>
          </form>
        )}
      </main>
      <footer className="text-center text-[11px] text-[#737373] py-6">
        © {new Date().getFullYear()} Forte Capital — Acesso privado.
      </footer>
    </div>
  );
}
